import { Injectable } from '@angular/core'
import { Observable, of, throwError } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { GeoCoordinates, GEO_LOCATION_TYPES } from '../../geocode/types'
import { GenericCoordinates } from '../../location.types'
import { delayedRetry } from '../../../../shared/utils/delayedRetryOperator'
import { DrrHttpClient } from '../../../../http/drr-http-client'
import {
  GeocodeService,
  GeocodeResult,
  AwsGeocodeRequest,
  AwsReverseGeocodeRequest,
  AwsReverseGeocodeResponse,
  AwsReverseGeocodeResult,
  AwsReverseGeocodeIntersection
} from '../geocode.interface'
import { MAX_GEOCODE_REFERENCE_DISTANCE_METERS } from '../../location.constants'

/**
 * Maximum distance (meters) from the query position for an intersection to be
 * considered an operationally useful cross street. Intersections farther than
 * this are ignored (e.g. a cross street miles away does not help dispatch).
 * ~805 m ≈ 0.5 mi — close enough to keep the nearest operationally useful
 * intersection while dropping distant ones. Confirm with product.
 */
export const MAX_CROSS_STREET_DISTANCE_METERS = MAX_GEOCODE_REFERENCE_DISTANCE_METERS

@Injectable({ providedIn: 'root' })
export class AwsGeocodeService implements GeocodeService {
  retryDelay = 2000
  retries = 2

  constructor(private http: DrrHttpClient) {}

  _setConfig(config: { retryDelay?: number; retries?: number }): void {
    if (config.retryDelay !== undefined) {
      this.retryDelay = config.retryDelay
    }
    if (config.retries !== undefined) {
      this.retries = config.retries
    }
  }

  /**
   * Get location from address using AWS Location Service geocode endpoint
   */
  getLocationFromAddress(address: string): Observable<Array<GeocodeResult>> {
    if (!address) {
      return of(null)
    }

    return of(address)
      .pipe(
        switchMap((addr) => this._getLocationFromAddress(addr)),
        delayedRetry(this.retryDelay, this.retries)
      )
  }

  private _getLocationFromAddress(address: string): Observable<Array<GeocodeResult>> {
    const requestBody: AwsGeocodeRequest = {
      text: address
    }

    return this.http
      .post<AwsReverseGeocodeResponse>('bff/roadservice/geo/geocode', requestBody)
      .pipe(
        switchMap((response) => {
          if (!response.ResultItems || response.ResultItems.length === 0) {
            return throwError('ZERO_RESULTS')
          }

          return of(
            response.ResultItems.map(result =>
              this.transformAwsGeocodeToGeocodeResult(result)
            )
          )
        })
      )
  }

  private transformAwsGeocodeToGeocodeResult(awsResult: AwsReverseGeocodeResult): GeocodeResult {
    const address = awsResult.Address
    const addressComponents = this.buildAddressComponentsFromAwsResult(awsResult)
    const formattedAddress = address?.Label || awsResult.Title || this.buildFormattedAddress(awsResult)

    return {
      formatted_address: formattedAddress,
      address_components: addressComponents,
      geometry: {
        location: {
          lat: () => awsResult.Position?.[1] || 0,
          lng: () => awsResult.Position?.[0] || 0
        },
        location_type: awsResult.PlaceType === 'PointAddress'
          ? GEO_LOCATION_TYPES.ROOFTOP
          : GEO_LOCATION_TYPES.APPROXIMATE
      },
      place_id: awsResult.PlaceId || '',
      types: awsResult.PlaceType ? [awsResult.PlaceType] : [],
      // DRRWEB-3399 — a searched-and-selected address is the only landing the reference strategies
      // act on, and it resolves through this forward geocode, so the `geocode` detector must
      // classify here too (PlaceType only; a forward result carries no Distance).
      ...this.buildReferenceFields(awsResult)
    }
  }

  /**
   * Get location from coordinates using AWS Location Service reverse geocode
   */
  getLocationFromCoords(coordinates: GeoCoordinates | GenericCoordinates): Observable<Array<GeocodeResult>> {
    if (!coordinates) {
      return of(null)
    }

    return of(coordinates)
      .pipe(
        switchMap((coords) => this._getLocationFromCoords(coords)),
        delayedRetry(this.retryDelay, this.retries)
      )
  }

  private _getLocationFromCoords(coordinates: GeoCoordinates | GenericCoordinates): Observable<Array<GeocodeResult>> {
    const latitude = this.getLatitude(coordinates)
    const longitude = this.getLongitude(coordinates)

    if (latitude === null || longitude === null) {
      return throwError('Invalid coordinates')
    }

    const requestBody: AwsReverseGeocodeRequest = {
      latitude: parseFloat(latitude.toString()),
      longitude: parseFloat(longitude.toString()),
      includeIntersections: true
    }

    return this.http
      .post<AwsReverseGeocodeResponse>('bff/roadservice/geo/reverse-geocode', requestBody)
      .pipe(
        switchMap((response) => {
          if (!response.ResultItems || response.ResultItems.length === 0) {
            return throwError('ZERO_RESULTS')
          }

          return of(
            response.ResultItems.map(result =>
              this.transformAwsReverseGeocodeToGeocodeResult(result, coordinates)
            )
          )
        })
      )
  }

  private transformAwsReverseGeocodeToGeocodeResult(
    awsResult: AwsReverseGeocodeResult,
    originalCoordinates: GeoCoordinates | GenericCoordinates
  ): GeocodeResult {
    const address = awsResult.Address
    const addressComponents = this.buildAddressComponentsFromAwsResult(awsResult)
    const formattedAddress = address?.Label || awsResult.Title || this.buildFormattedAddress(awsResult)

    const lat = this.getLatitude(originalCoordinates)
    const lng = this.getLongitude(originalCoordinates)

    return {
      formatted_address: formattedAddress,
      address_components: addressComponents,
      geometry: {
        location: {
          lat: () => awsResult.Position?.[1] || lat,
          lng: () => awsResult.Position?.[0] || lng
        },
        location_type: awsResult.PlaceType === 'PointAddress'
          ? GEO_LOCATION_TYPES.ROOFTOP
          : GEO_LOCATION_TYPES.APPROXIMATE
      },
      place_id: awsResult.PlaceId || '',
      types: awsResult.PlaceType ? [awsResult.PlaceType] : [],
      ...this.buildCrossStreets(awsResult),
      ...this.buildReferenceFields(awsResult)
    }
  }

  /**
   * DRRWEB-3662 — carries the raw `PlaceType` of every geocode result, unconditionally, the same
   * way buildCrossStreets always runs. The `geocode` strategy maps it to a target zoom and every
   * landing analytics row is broken down by it. Whether it decides the initial zoom is gated
   * later, by the active landmark-zoom A/B arm's strategy chain.
   */
  private buildReferenceFields(
    awsResult: AwsReverseGeocodeResult
  ): { placeType?: string } {
    return {
      ...(awsResult.PlaceType ? { placeType: awsResult.PlaceType } : {}),
    }
  }

  private buildCrossStreets(
    awsResult: AwsReverseGeocodeResult
  ): { crossStreet?: string; crossStreet2?: string } {
    const labels: string[] = []
    for (const intersection of awsResult.Intersections ?? []) {
      const withinCap =
        intersection.Distance === undefined ||
        intersection.Distance <= MAX_CROSS_STREET_DISTANCE_METERS
      if (!withinCap) {
        continue
      }
      const label = this.formatIntersection(intersection)
      if (label) {
        labels.push(label)
      }
      if (labels.length === 2) {
        break
      }
    }

    const [crossStreet, crossStreet2] = labels
    return {
      ...(crossStreet && { crossStreet }),
      ...(crossStreet2 && { crossStreet2 })
    }
  }

  private formatIntersection(intersection: AwsReverseGeocodeIntersection): string {
    // AWS may repeat street names (e.g. ["Main St","Main St","Oak Ave","Oak Ave"]).
    const deduped = [...new Set((intersection.Address?.Intersection ?? []).filter(Boolean))]
    return deduped.join(' & ')
  }

  private buildAddressComponentsFromAwsResult(awsResult: AwsReverseGeocodeResult): Array<{
    long_name: string
    short_name: string
    types: string[]
  }> {
    const components = []
    const address = awsResult.Address

    if (!address) {
      return components
    }

    if (address.AddressNumber) {
      components.push({
        long_name: address.AddressNumber,
        short_name: address.AddressNumber,
        types: ['street_number']
      })
    }

    if (address.Street) {
      components.push({
        long_name: address.Street,
        short_name: address.Street,
        types: ['route']
      })
    }

    if (address.District) {
      components.push({
        long_name: address.District,
        short_name: address.District,
        types: ['neighborhood', 'political']
      })
    }

    if (address.Locality) {
      components.push({
        long_name: address.Locality,
        short_name: address.Locality,
        types: ['locality', 'political']
      })
    }

    if (address.Region) {
      components.push({
        long_name: address.Region.Name || address.Region.Code || '',
        short_name: address.Region.Code || address.Region.Name || '',
        types: ['administrative_area_level_1', 'political']
      })
    }

    if (address.PostalCode) {
      components.push({
        long_name: address.PostalCode,
        short_name: address.PostalCode,
        types: ['postal_code']
      })
    }

    if (address.Country) {
      components.push({
        long_name: address.Country.Name || '',
        short_name: address.Country.Code2 || address.Country.Code3 || '',
        types: ['country', 'political']
      })
    }

    return components
  }

  private buildFormattedAddress(awsResult: AwsReverseGeocodeResult): string {
    const address = awsResult.Address
    if (!address) {
      return awsResult.Title || ''
    }

    const parts = []

    if (address.AddressNumber && address.Street) {
      parts.push(`${address.AddressNumber} ${address.Street}`)
    } else if (address.Street) {
      parts.push(address.Street)
    }

    if (address.Locality) {
      parts.push(address.Locality)
    }

    if (address.Region?.Name || address.Region?.Code) {
      parts.push(address.Region.Name || address.Region.Code)
    }

    if (address.PostalCode) {
      parts.push(address.PostalCode)
    }

    if (address.Country?.Name) {
      parts.push(address.Country.Name)
    }

    return parts.join(', ')
  }

  private getLatitude(coordinates: GeoCoordinates | GenericCoordinates): number | null {
    if ('lat' in coordinates && typeof coordinates.lat === 'number') {
      return coordinates.lat
    }
    if ('latitude' in coordinates) {
      return typeof coordinates.latitude === 'string'
        ? parseFloat(coordinates.latitude)
        : coordinates.latitude as number
    }
    return null
  }

  private getLongitude(coordinates: GeoCoordinates | GenericCoordinates): number | null {
    if ('lng' in coordinates && typeof coordinates.lng === 'number') {
      return coordinates.lng
    }
    if ('longitude' in coordinates) {
      return typeof coordinates.longitude === 'string'
        ? parseFloat(coordinates.longitude)
        : coordinates.longitude as number
    }
    return null
  }
}

